import { ApiError } from '../api/client';

/** Same 403/400/fallback idiom `calendar-api-errors.ts`'s `genericApiErrorMessage` already applies. */
function genericApiErrorMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/**
 * `422 ORDER_CUTOFF_PASSED` drives its own UI branch (the override-reason
 * field) rather than the generic inline `Alert` every other error code
 * gets — both `OrderForm` and `OrderDetailPage`'s edit mode need to tell
 * this one code apart from the rest of `mapOrderError`.
 */
export function isCutoffPassedError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const code = (error.body as { code?: string } | null)?.code;

  return error.status === 422 && code === 'ORDER_CUTOFF_PASSED';
}

/** `orders/spec.md` → codes from `OrdersExceptionFilter` (`design.md` §6, reconciled against `orders/spec.md`'s literal wording in `apply-progress.md` PR 2). */
export function mapOrderError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'ORDER_ALREADY_EXISTS') {
    return 'Ya existe un pedido activo para esa empresa, sede y fecha.';
  }

  if (error.status === 422 && code === 'SERVICE_DATE_NOT_AVAILABLE') {
    return 'Esa fecha no es un día de servicio habilitado para el contrato.';
  }

  if (error.status === 422 && code === 'ORDER_MENU_NOT_AVAILABLE') {
    return 'Uno de los menús seleccionados no está disponible para esa fecha.';
  }

  if (error.status === 422 && code === 'ORDER_QUANTITY_LIMIT_EXCEEDED') {
    return 'La cantidad supera el tope máximo diario del contrato.';
  }

  if (error.status === 422 && code === 'ORDER_USER_QUANTITY_LIMIT_EXCEEDED') {
    return 'La cantidad supera tu tope diario asignado.';
  }

  if (error.status === 422 && code === 'OPERATING_UNIT_REQUIRED') {
    return 'El cliente tiene más de un contrato vigente: elegí el punto de venta.';
  }

  if (error.status === 422 && code === 'NO_ACTIVE_CONTRACT') {
    return 'El cliente no tiene un contrato vigente en ese punto de venta.';
  }

  if (error.status === 422 && code === 'ORDER_CUTOFF_PASSED') {
    return 'Ya pasó la hora de corte para esta fecha. Necesitás autorización para continuar.';
  }

  if (error.status === 422 && code === 'ORDER_OVERRIDE_REASON_REQUIRED') {
    return 'Indicá el motivo de la excepción de corte.';
  }

  if (error.status === 422 && code === 'MENU_QUOTA_EXCEEDED') {
    return 'No hay cupo suficiente para uno de los menús pedidos.';
  }

  if (error.status === 409 && code === 'ORDER_LOCKED') {
    return 'Este pedido ya está confirmado y no se puede editar por esta vía.';
  }

  if (error.status === 422 && code === 'ORDER_EMPTY') {
    return 'El pedido no tiene líneas cargadas. Agregá al menos un menú.';
  }

  if (error.status === 422 && code === 'ORDER_CANCELLATION_REASON_REQUIRED') {
    return 'Indicá el motivo de la cancelación.';
  }

  if (error.status === 422 && code === 'ORDER_TEMPLATE_INVALID_DAYS') {
    return 'Los días de la semana deben estar entre lunes y domingo.';
  }

  if (error.status === 422 && code === 'MENU_POSITION_INACTIVE') {
    return 'Una de las posiciones seleccionadas está desactivada.';
  }

  if (error.status === 422 && code === 'ORDER_TEMPLATE_DUPLICATE_POSITION') {
    return 'No podés repetir la misma posición en más de una línea.';
  }

  if (error.status === 409 && code === 'ORDER_CHANGE_INVALID_STATUS') {
    return 'Esta solicitud o pedido ya no admite esta acción.';
  }

  if (error.status === 409 && code === 'ORDER_CHANGE_SELF_APPROVAL') {
    return 'No podés aprobar una solicitud que vos mismo creaste.';
  }

  if (error.status === 409 && code === 'ORDER_CHANGE_ALREADY_PENDING') {
    return 'Ya hay una solicitud de cambio pendiente para este pedido.';
  }

  if (error.status === 422 && code === 'ORDER_CHANGE_REASON_REQUIRED') {
    return 'Indicá el motivo del rechazo.';
  }

  return genericApiErrorMessage(error);
}
